import { useCallback, useEffect, useState } from "react";
import { HGIntervalType } from "@/common/types";
import { SpanningMode } from "@/common/recordTree";

const STORAGE_KEY = "eventera.recordsView";

export type RecordsMode = "time" | "notes";
export type SortOrder = "asc" | "desc";
export type NotesSortField = "created" | "modified";
/** How far down a collection's subtree the record list reaches. */
export type CollectionScope = "direct" | "children" | "all";

export interface RecordsViewOptions {
  mode: RecordsMode;
  /** Interval type for the first level below the active interval. */
  groupBy: "auto" | HGIntervalType;
  /** Interval type for the level below that, or "none" to stop. */
  thenBy: "auto" | "none" | HGIntervalType;
  order: SortOrder;
  notesSort: NotesSortField;
  notesOrder: SortOrder;
  /** Where a record crossing several sub-intervals is listed. */
  spanning: SpanningMode;
  showGaps: boolean;
  /**
   * On a collection page, which records the list reaches: only the collection's
   * own ("direct"), plus its immediate child collections ("children"), or the
   * whole subtree ("all"). Ignored outside a collection.
   */
  collectionScope: CollectionScope;
}

export const defaultRecordsViewOptions: RecordsViewOptions = {
  mode: "time",
  groupBy: "auto",
  thenBy: "auto",
  order: "asc",
  notesSort: "created",
  notesOrder: "desc",
  spanning: "ends",
  showGaps: true,
  collectionScope: "direct",
};

function readStored(): RecordsViewOptions {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultRecordsViewOptions;
    // Merged over the defaults so options added later never break a stored view.
    return { ...defaultRecordsViewOptions, ...JSON.parse(raw) };
  } catch {
    return defaultRecordsViewOptions;
  }
}

/**
 * Toolbar state for the Records view, persisted to localStorage.
 *
 * Deliberately not in Redux: nothing outside the Records view reads it, and it
 * has to survive a reload, which Redux state here does not.
 */
export function useRecordsViewOptions() {
  const [options, setOptions] = useState<RecordsViewOptions>(readStored);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(options));
    } catch {
      // A full or disabled storage costs the user persistence, not the view.
    }
  }, [options]);

  const setOption = useCallback(
    <K extends keyof RecordsViewOptions>(key: K, value: RecordsViewOptions[K]) =>
      setOptions((previous) => ({ ...previous, [key]: value })),
    [],
  );

  return { options, setOption };
}
